import '@livekit/components-styles';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingBoundary } from 'LoadingBoundary';
import { generateGuid } from 'guid';
import { useEventLogger } from 'FrontRoyalAngular';
import { useSendPing } from 'Status';
import { useDispatch } from 'react-redux';
import { type ConnectLiveKitRoomProps } from '../TutorBotVoiceAgent.types';
import { LiveKitRoomConversation } from './LiveKitRoomConversation';
import { StartScreen } from './StartScreen/StartScreen';
import { PostDiscussion } from './PostDiscussion/PostDiscussion';
import { useLogLaunchEvent } from '../hooks/useLogLaunchEvent';
import { getLogInfo } from '../utils/getLogInfo';
import { voiceAgentSlice } from '../redux/voiceAgent';

type PropsPassedThroughToConnectLiveKitRoom = Omit<ConnectLiveKitRoomProps, 'conversationId' | 'onDisconnected'>;
type Props = {
    IntroComponent: React.ReactNode;
    toggleAudioOrText: () => void;
    autoStart?: boolean;
} & PropsPassedThroughToConnectLiveKitRoom;

type Phase = typeof PostDiscussion | typeof LiveKitRoomConversation | typeof StartScreen | null;

function useSetIsRoomOpen({ conversationId, phase }: { conversationId: string; phase: Phase }) {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            voiceAgentSlice.actions.updateConversationMetadata({
                conversationId,
                roomIsOpen: phase === LiveKitRoomConversation,
            }),
        );

        return () => {
            dispatch(
                voiceAgentSlice.actions.updateConversationMetadata({
                    conversationId,
                    roomIsOpen: false,
                }),
            );
        };
    }, [phase, conversationId, dispatch]);
}

function usePingOnConversationComplete({ phase }: { phase: Phase }) {
    const { sendPing } = useSendPing();
    const prevPhaseRef = useRef<Phase>(null);

    useEffect(() => {
        const prevValue = prevPhaseRef.current;

        // When the conversation is done, trigger a ping. This ensures that messages are saved and
        // tutor_bot_voice_agent_seconds_used_this_week gets pulled from the server so we can show
        // the modal telling the user that their weekly voice minutes are up if necessary.
        // There is one edge case here. If the last message:sent event that is logged from the conversation
        // pushed the user over the limit, this ping might not catch it. In that case, the modal will
        // not show until the subseqent ping.
        if (prevValue === LiveKitRoomConversation && phase !== LiveKitRoomConversation) {
            sendPing();
        }

        // Update the ref AFTER the check
        prevPhaseRef.current = phase;
    }, [phase, sendPing]);
}

const VoiceAgentRoomComponent = ({
    agentIdentifier,
    extraTokenAttributes,
    uiContext,
    IntroComponent,
    toggleAudioOrText,
    autoStart = false,
}: Props) => {
    const [conversationId, setConversationId] = useState<string>(() => generateGuid());
    const [conversationComplete, setConversationComplete] = useState<boolean>();
    const [shouldConnectVoiceRoom, setShouldConnectVoiceRoom] = useState<boolean>(autoStart);
    const EventLogger = useEventLogger();
    const phase = useMemo(
        () => (conversationComplete ? PostDiscussion : shouldConnectVoiceRoom ? LiveKitRoomConversation : StartScreen),
        [conversationComplete, shouldConnectVoiceRoom],
    );
    useSetIsRoomOpen({ conversationId, phase });
    usePingOnConversationComplete({ phase });

    useLogLaunchEvent({
        eventType: 'tutorbot:opened_voice_agent_room',
        agentIdentifier,
        uiContext,
        extraTokenAttributes,
    });

    const onDisconnected = useCallback(() => {
        setConversationComplete(true);
    }, []);

    useEffect(() => {
        if (autoStart) setShouldConnectVoiceRoom(true);
    }, [autoStart]);

    const restartDiscussion = useCallback(() => {
        setConversationComplete(false);
        setShouldConnectVoiceRoom(true);
        setConversationId(generateGuid());
        EventLogger.log(
            'tutorbot:restart_voice_agent_conversation_clicked',
            getLogInfo({ agentIdentifier, extraTokenAttributes, uiContext }),
        );
    }, [setShouldConnectVoiceRoom, EventLogger, agentIdentifier, extraTokenAttributes, uiContext]);

    return (
        <LoadingBoundary>
            {phase === PostDiscussion ? (
                <PostDiscussion conversationId={conversationId} restartDiscussion={restartDiscussion} />
            ) : phase === LiveKitRoomConversation ? (
                <LiveKitRoomConversation
                    uiContext={uiContext}
                    agentIdentifier={agentIdentifier}
                    extraTokenAttributes={extraTokenAttributes}
                    toggleAudioOrText={toggleAudioOrText}
                    conversationId={conversationId}
                    onDisconnected={onDisconnected}
                />
            ) : (
                <StartScreen
                    IntroComponent={IntroComponent}
                    onStartDiscussionClick={() => setShouldConnectVoiceRoom(true)}
                    toggleAudioOrText={toggleAudioOrText}
                />
            )}
        </LoadingBoundary>
    );
};

export const VoiceAgentRoom = memo(VoiceAgentRoomComponent) as typeof VoiceAgentRoomComponent;
